import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Alert,
    Body1,
    Button,
    FullBleed,
    Heading1,
    HorizontalDivider,
    Modal,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getFragment } from '../../../../../graphql/client/helpers/get-fragment';
import type {
    PatientInfoFragment,
    UnclaimedPrescriberOrderInfoFragment,
} from '../../../../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../../../../util/get-absolute-route';
import { UnclaimedPrescriptionDescription } from '../../UnclaimedPrescriptionDescription';

import { MemberSelection } from './MemberSelection';

type VerifyPatientProps = {
    prescriberOrder: UnclaimedPrescriberOrderInfoFragment;
    claimPrescription: (patient: PatientInfoFragment) => Promise<void>;
};

export const VerifyPatient = (props: VerifyPatientProps) => {
    const { prescriberOrder, claimPrescription } = props;
    const { t } = useTranslation();
    const [selectedPatient, setSelectedPatient] =
        useState<PatientInfoFragment | null>(null);
    const [claimError, setClaimError] = useState<string | null>(null);
    const [isClaimLoading, setIsClaimLoading] = useState(false);
    const [opened, { open, close }] = useDisclosure(false);

    const handleSelectPatient = (patient: PatientInfoFragment) => {
        setSelectedPatient(patient);
        open();
    };
    const handleClaimPrescription = async () => {
        if (selectedPatient) {
            setIsClaimLoading(true);
            setClaimError(null);
            try {
                await claimPrescription(selectedPatient);
            } catch {
                setClaimError(t('verifyPatient.error'));
                setIsClaimLoading(false);
            }
        }
    };

    return (
        <>
            {claimError && (
                <FullBleed breakpoint="md" color="error">
                    <Alert severity="error">{claimError}</Alert>
                </FullBleed>
            )}
            <Stack gap="lg">
                <Stack gap="sm">
                    <Heading1>{t('verifyPatient.title')}</Heading1>
                    <Body1>{t('verifyPatient.subtitle')}</Body1>{' '}
                </Stack>
                <UnclaimedPrescriptionDescription
                    prescriberOrder={prescriberOrder}
                />
                <HorizontalDivider />
                <Stack gap="sm">
                    {prescriberOrder.matchingPatientsConnection?.edges.map(
                        (x) => (
                            <MemberSelection
                                key={getFragment(x.node).id}
                                patient={getFragment(x.node)}
                                relationshipType={
                                    x.relationship.relationshipType
                                }
                                selectPatient={handleSelectPatient}
                            />
                        )
                    )}
                </Stack>
                <Button
                    link
                    size="lg"
                    variant="outline"
                    to={getAbsoluteRoute('order.prescriberOrderId.verify', {
                        prescriberOrderId:
                            prescriberOrder.drxId ?? prescriberOrder.id,
                    })}
                >
                    {t('verifyPatient.forSomeoneElse')}
                </Button>
                <Modal
                    onClose={close}
                    opened={opened || isClaimLoading}
                    title={t('verifyPatient.confirm.title')}
                >
                    <Stack gap="lg">
                        <Stack gap="sm">
                            <Body1>
                                {t('verifyPatient.confirm.verifyFor', {
                                    name: selectedPatient?.fullName,
                                })}
                            </Body1>
                            <UnclaimedPrescriptionDescription
                                prescriberOrder={prescriberOrder}
                            />
                        </Stack>
                        <Stack gap="sm">
                            <Button
                                onClick={handleClaimPrescription}
                                loading={isClaimLoading}
                            >
                                {t('verifyPatient.confirm.confirm')}
                            </Button>
                            <Button variant="outline" onClick={close}>
                                {t('verifyPatient.confirm.cancel')}
                            </Button>
                        </Stack>
                    </Stack>
                </Modal>
            </Stack>
        </>
    );
};
